import { Phone, Mail, MapPin, Clock, Facebook, MessageCircle } from "lucide-react";
import { CLINIC, buildWaUrl } from "@/lib/data";

function ContactCard({ title, accent, phone, phoneRaw, emails, facebook, waUrl, testId }) {
  const isBlue = accent === "blue";
  return (
    <div
      data-testid={testId}
      className="bg-white rounded-[28px] p-7 lg:p-8 border border-slate-100 card-shadow hover:card-shadow-hover transition-all duration-500"
    >
      <span className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold mb-4 ${isBlue ? "text-brand-blue" : "text-brand-aquaDark"}`}>
        <span className={`w-2 h-2 rounded-full ${isBlue ? "bg-brand-blue" : "bg-brand-aqua"}`} />
        {isBlue ? "Stomatologie" : "Recuperare medicală"}
      </span>
      <h3 className="font-display text-[24px] lg:text-[26px] font-medium text-slate-900 mb-6">
        {title}
      </h3>
      <div className="space-y-4">
        <a
          href={`tel:${phoneRaw}`}
          className="flex items-center gap-3 group"
          data-testid={`${testId}-phone`}
        >
          <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isBlue ? "bg-brand-soft text-brand-blue" : "bg-[#F2F9DD] text-brand-aquaDark"}`}>
            <Phone className="w-4.5 h-4.5" strokeWidth={1.8} />
          </span>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Telefon / WhatsApp</p>
            <p className="font-display text-[18px] font-medium text-slate-900 group-hover:text-brand-blue transition-colors">
              {phone}
            </p>
          </div>
        </a>

        <div className="flex items-start gap-3">
          <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isBlue ? "bg-brand-soft text-brand-blue" : "bg-[#F2F9DD] text-brand-aquaDark"}`}>
            <Mail className="w-4.5 h-4.5" strokeWidth={1.8} />
          </span>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Email</p>
            <div className="flex flex-col">
              {emails.map((email) => (
                <a
                  key={email}
                  href={`mailto:${email}`}
                  className="text-[14px] text-slate-700 hover:text-brand-blue transition-colors break-all"
                >
                  {email}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100">
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-white text-[13px] font-medium transition-all ${isBlue ? "bg-brand-blue hover:bg-brand-blueDark" : "bg-brand-aqua hover:bg-brand-aquaDark"}`}
            data-testid={`${testId}-whatsapp`}
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
          <a
            href={facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-slate-700 border border-slate-200 hover:border-slate-300 text-[13px] font-medium transition-colors"
            data-testid={`${testId}-facebook`}
          >
            <Facebook className="w-4 h-4" />
            Facebook
          </a>
        </div>
      </div>
    </div>
  );
}

export default function Contact() {
  const dentalWa = buildWaUrl(CLINIC.dental.waNumber, "Bună ziua! Doresc o programare la Marimar Dental Clinic.");
  const fizioWa = buildWaUrl(CLINIC.fizio.waNumber, "Bună ziua! Doresc o programare la Marimar Fizio & Kinetoterapie.");

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 lg:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mb-12">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-blue mb-5">
              <span className="w-6 h-px bg-brand-blue" />
              Contact
            </span>
            <h2 className="font-display text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.05] font-light text-slate-900 tracking-tight mb-6 text-balance">
              Vino să ne <span className="text-gradient-blue font-medium">cunoști</span> personal.
            </h2>
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-brand-soft text-brand-blue flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Adresă</p>
                  <p className="text-[15px] text-slate-800 font-medium">{CLINIC.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-brand-soft text-brand-blue flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Program</p>
                  <p className="text-[15px] text-slate-800">{CLINIC.hours.weekdays}</p>
                  <p className="text-[15px] text-slate-800">{CLINIC.hours.saturday}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            <ContactCard
              title={CLINIC.dental.label}
              accent="blue"
              phone={CLINIC.dental.phone}
              phoneRaw={CLINIC.dental.phoneRaw}
              emails={CLINIC.dental.emails}
              facebook={CLINIC.dental.facebook}
              waUrl={dentalWa}
              testId="contact-card-dental"
            />
            <ContactCard
              title={CLINIC.fizio.label}
              accent="aqua"
              phone={CLINIC.fizio.phone}
              phoneRaw={CLINIC.fizio.phoneRaw}
              emails={CLINIC.fizio.emails}
              facebook={CLINIC.fizio.facebook}
              waUrl={fizioWa}
              testId="contact-card-fizio"
            />
          </div>
        </div>

        {/* Google Maps */}
        <div className="rounded-[28px] overflow-hidden border border-slate-100 card-shadow" data-testid="contact-map">
          <iframe
            title="Marimar Clinic – Șoseaua Andronache 52-54, Sector 2, București"
            src="https://www.google.com/maps?q=Soseaua+Andronache+52-54,+Sector+2,+Bucuresti&output=embed"
            width="100%"
            height="420"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}
